import {EntityType} from '@/forecast-app/admin-tab/custom-fields-page/Constants';
import {hasFeatureFlag} from '@/forecast-app/shared/util/FeatureUtil';
import {hasNewApprovalStatuses} from '@/forecast-app/shared/util/ApprovalStatusUtil';
import {useMemo, useEffect} from 'react';
import {CUSTOM_FIELD_PREFIX} from '../../../../../project-tab/projects/scoping-page/ProjectScopingUtil';
import {Column, useTimesheetTableContext} from '../../TimesheetTableContext';
import {useVisibleTimesheetTableColumns} from '../useVisibleTimesheetTableColumns';
import {CustomFieldDefinition} from '@/forecast-app/admin-tab/custom-fields-page/types';

type UseTimesheetsTaskTableColumnsProps = {
	viewer: {
		harvestUser: boolean | null;
		unit4User: boolean | null;
		company: {harvestEnabled: boolean | null; unit4Enabled: boolean | null} | null;
	};
	selectedPerson: {harvestUser: boolean | null; unit4User: boolean | null};
	isWeekView: boolean;
	customFieldDefinitions: CustomFieldDefinition[];
	/**
	 * The company's Timesheet approval toggle. The audit-log ('log') column exists only for time
	 * that goes through approval, and its cell in TimesheetsTimeRowTable renders empty without
	 * this. PIKA-714: the column's visibility used to read the feature flags alone, so a company
	 * with Timesheet approval off (but Time off approval on — a supported combination) reserved a
	 * column of width for cells that could never draw anything.
	 */
	usingTimeApproval: boolean;
};

export function useTimesheetsTaskTableColumns({
	viewer,
	selectedPerson,
	isWeekView,
	customFieldDefinitions,
	usingTimeApproval,
}: UseTimesheetsTaskTableColumnsProps): Column[] {
	const {setColumnDefinitions} = useTimesheetTableContext();
	const showHarvestColumn = !!(viewer.company?.harvestEnabled && selectedPerson.harvestUser);
	const showUnit4Column = !!(viewer.company?.unit4Enabled && selectedPerson.unit4User);

	const allTaskColumns = useMemo(
		() => [
			{id: 'switch-task-indicator', visible: true, toggleable: false},
			{
				id: 'project-indicator',
				visible: true,
				translationId: 'overview_time.card_details_selector_id',
				toggleable: true,
			},
			{id: 'project-name', visible: true, translationId: 'project_section.project_name', toggleable: true},
			{id: 'task-name', visible: true, toggleable: false},
			{id: 'phase', visible: true, translationId: 'common.phase', toggleable: true},
			{id: 'client', visible: false, translationId: 'common.client', toggleable: true},
			{id: 'remaining', visible: false, translationId: 'common.remaining', toggleable: true},
			...(!isWeekView
				? customFieldDefinitions
						.filter(customFieldDefinition => customFieldDefinition.entityType === EntityType.TIME_REG) //TODO: add backend filter for time reg custom fields
						.map<Column>(customFieldDefinition => {
							return {
								id: CUSTOM_FIELD_PREFIX + customFieldDefinition.key,
								visible: true,
								displayName: customFieldDefinition.displayName,
								customFieldDefinition,
								toggleable: true,
							};
						})
				: []),
			{id: 'hour-inputs-week', visible: isWeekView, toggleable: false},
			{id: 'hour-inputs-day', visible: !isWeekView, toggleable: false},
			{id: 'notes-button', visible: isWeekView, toggleable: false},
			{id: 'notes-input', visible: !isWeekView, toggleable: false},
			{id: 'harvest', visible: showHarvestColumn, toggleable: false},
			{id: 'unit4', visible: showUnit4Column, toggleable: false},
			{
				id: 'log',
				// PIKA-793: the audit-log flag stays a raw read — it is the drawer's own flag and
				// carries no Sage meaning. Only the new-statuses half moves to the Sage-aware helper.
				visible: usingTimeApproval && hasFeatureFlag('timesheet_approval_audit_log') && hasNewApprovalStatuses(),
				toggleable: false,
			},
			{id: 'star', visible: true, toggleable: false},
			{id: 'edit', visible: !isWeekView, toggleable: false},
			{id: 'delete', visible: true, toggleable: false},
		],
		[viewer, selectedPerson, isWeekView, customFieldDefinitions, usingTimeApproval]
	);

	useEffect(() => setColumnDefinitions(allTaskColumns), [allTaskColumns]);

	const visibleTaskColumns = useVisibleTimesheetTableColumns(allTaskColumns);

	return visibleTaskColumns;
}
